'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  showNewProjectModal: boolean;
  setShowNewProjectModal: (show: boolean) => void;
  chatHistoryOpen: boolean;
  setChatHistoryOpen: (open: boolean) => void;
  chatHistoryPinned: boolean;
  setChatHistoryPinned: (pinned: boolean) => void;
  chatHistoryHovered: boolean;
  setChatHistoryHovered: (hovered: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';
const CHAT_HISTORY_PINNED_KEY = 'chat-history-pinned';

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [isCollapsed, setIsCollapsedState] = useState(() => {
    // 初始化时从 localStorage 读取状态
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return saved === 'true';
    }
    return false;
  });
  
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [chatHistoryHovered, setChatHistoryHovered] = useState(false);
  
  // 同步初始化 chatHistoryPinned 和 chatHistoryOpen
  const [chatHistoryPinned, setChatHistoryPinnedState] = useState(() => {
    // 初始化时从 localStorage 读取固定状态
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(CHAT_HISTORY_PINNED_KEY);
      return saved === 'true';
    }
    return false;
  });
  
  // 如果固定状态为 true，则 chatHistoryOpen 也应该初始化为 true
  const [chatHistoryOpen, setChatHistoryOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedPinned = localStorage.getItem(CHAT_HISTORY_PINNED_KEY);
      return savedPinned === 'true';
    }
    return false;
  });

  // 封装 setIsCollapsed，同时更新 localStorage
  const setIsCollapsed = (collapsed: boolean) => {
    setIsCollapsedState(collapsed);
    if (typeof window !== 'undefined') {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed.toString());
    }
  };

  // 封装 setChatHistoryPinned，同时更新 localStorage
  const setChatHistoryPinned = (pinned: boolean) => {
    setChatHistoryPinnedState(pinned);
    if (typeof window !== 'undefined') {
      localStorage.setItem(CHAT_HISTORY_PINNED_KEY, pinned.toString());
    }
    // 如果固定，则自动打开
    if (pinned) {
      setChatHistoryOpen(true);
    }
  };

  // 监听 storage 事件，以便在多个标签页之间同步状态
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SIDEBAR_COLLAPSED_KEY) {
        setIsCollapsedState(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <SidebarContext.Provider value={{ 
      isCollapsed, 
      setIsCollapsed, 
      showNewProjectModal, 
      setShowNewProjectModal,
      chatHistoryOpen,
      setChatHistoryOpen,
      chatHistoryPinned,
      setChatHistoryPinned,
      chatHistoryHovered,
      setChatHistoryHovered
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};